/**
 * Global 403 → Sonner interceptor (STORY-008-006 AC#6).
 *
 * Subscribes to the shared TanStack Query QueryCache AND MutationCache; when a
 * query or mutation transitions into an error whose cause is a 403
 * `authz.permission_denied` `ApiError`, it invokes `onForbidden` once with the
 * server problem detail. Mutations are the primary case — an admin who clicks
 * an action they lack the slug for (e.g. Approve without `sellers.approve`)
 * only learns of the denial at the network layer. Page entry is already guarded
 * by `RequireAdminAuth` (shell boundary) and `RequirePermissionRoute` (per-page
 * slug, slug-named toast); this is the catch-all for in-page actions.
 *
 * Why a runtime cache subscription rather than `new QueryClient({ queryCache:
 * new QueryCache({ onError }) })`: the singleton `queryClient` is constructed
 * once in `lib/queryClient.ts` and shared by the whole app (out of this task's
 * allowed-touch). `getQueryCache().subscribe()` / `getMutationCache().
 * subscribe()` install at admin-shell mount and the returned unsubscribe tears
 * them down, scoping the toast to admin sessions without re-instantiating the
 * client. (See task brief "Why a cache subscription".)
 *
 * Matching: the backend's RBAC denial is the canonical `403` +
 * `code === "authz.permission_denied"` (apps/api/garden/auth/rbac.py
 * `PermissionDeniedError`). We require BOTH so an unrelated 403 (CSRF, etc.)
 * does not masquerade as a permission toast. The slug lives inside the human
 * `detail` string, not a machine field, so the hook renders a slug-less
 * localized title with the server `detail` as the description.
 */
import { useEffect } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api-client";
import { queryClient } from "@/lib/queryClient";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";

/** Canonical backend code for an RBAC 403 (apps/api/garden/auth/rbac.py). */
const PERMISSION_DENIED_CODE = "authz.permission_denied";

/**
 * Stable Sonner toast id so several queries/mutations failing with 403 in the
 * same tick collapse into a single toast instead of stacking (no-spam).
 */
const PERMISSION_DENIED_TOAST_ID = "admin-permission-denied";

/** True only for the backend's canonical RBAC permission-denied 403. */
function isPermissionDenied(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.code === PERMISSION_DENIED_CODE
  );
}

/**
 * Subscribe to query + mutation error transitions and invoke `onForbidden`
 * once per error that is a canonical RBAC 403. Returns an unsubscribe that
 * tears down BOTH subscriptions.
 */
export function installQueryErrorInterceptor(
  onForbidden: (detail: string) => void,
): () => void {
  const fire = (error: unknown): void => {
    if (isPermissionDenied(error)) {
      onForbidden(error.problem.detail ?? error.code ?? "");
    }
  };

  const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated") return;
    if (event.action.type !== "error") return;
    fire(event.query.state.error);
  });

  const unsubscribeMutations = queryClient
    .getMutationCache()
    .subscribe((event) => {
      if (event.type !== "updated") return;
      if (event.action.type !== "error") return;
      fire(event.mutation.state.error);
    });

  return () => {
    unsubscribeQueries();
    unsubscribeMutations();
  };
}

/**
 * Admin-shell hook: wire the network-level 403 interceptor to a localized
 * Sonner toast for the lifetime of the mounting component (Task 10 mounts this
 * in `AdminLayout`). The subscription is installed on mount and removed on
 * unmount, so the toast is scoped to admin sessions.
 */
export function useQueryErrorInterceptor(): void {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  useEffect(() => {
    return installQueryErrorInterceptor((detail) => {
      toast.error(t("error.permissionDenied"), {
        id: PERMISSION_DENIED_TOAST_ID,
        ...(detail ? { description: detail } : {}),
      });
    });
  }, [t]);
}
